import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ArrowLeftOutlined, NumberOutlined, PlusOutlined } from '@ant-design/icons';

import {
    Button,
    Card,
    Col,
    Divider,
    Input,
    message,
    Row,
    Table,
    Form,
    notification,
    Checkbox,
    Typography,
    Skeleton,
    Spin,
} from 'antd';
import { Price } from 'components/Display';
import RecordFooter from 'components/shared/RecordFooter';
import { AddressProps, UploadFile, SquarePaymentForm, FormErrorParser } from '@partsbadger/library';
import UIStore from '@/stores/UIStore';
import Address from 'components/shared/Address';
import UserStore from 'stores/UserStore';
import ErrorRender from 'components/shared/ErrorRender';
import SalesOrderStore from '../../stores/SalesOrderStore';
import { ColumnProps } from 'antd/es/table';
import { OrderLineItemProps } from '../../types/types';
import { observer } from 'mobx-react';
import { allowed_2d, allowed_3d, SQUARE_APP_ID, SQUARE_ENVIRONMENT_URL, SQUARE_LOCATION_ID } from '@partsbadger/utils';
import { PageHeader } from '../../components/PageHeader';
import Addresses from '../../components/SalesOrders/Addresses';
import { PartPreview } from '../../components/Quotes';

const ButtonGroup = Button.Group;
const { Title } = Typography;

interface IParams {
    id: any;
}

const Reorder = ({ ...props }) => {
    const [loading, setLoading] = useState(false);
    const [billing_address, setBillingAddress] = useState<AddressProps>();
    const [shipping_address, setShippingAddress] = useState<AddressProps>();
    const [errors, setErrors] = useState<any>();
    const [form] = Form.useForm();

    const history = useHistory();

    const { order, order_items } = SalesOrderStore;

    const { id } = useParams<IParams>();

    const getOrderDetails = () => {
        SalesOrderStore.getOrderById(id);
    };
    useEffect(() => {
        UIStore.setDocumentTitle('Reordering');
        getOrderDetails();
    }, []);

    const buttonReference = React.useRef<HTMLButtonElement>(null);

    const handleSubmit = async (creditCardToken?: string) => {
        form.validateFields()
            .then(async values => {
                const order = SalesOrderStore?.order;
                const order_items = SalesOrderStore?.order_items;

                const payload = {
                    billing_name: order?.billing_name,
                    billing_street: order?.billing_street,
                    billing_city: order?.billing_city,
                    billing_country: order?.billing_country,
                    billing_state: order?.billing_state,
                    billing_zip_code: order?.billing_zip_code,
                    shipping_name: order?.shipping_name,
                    shipping_street: order?.shipping_street,
                    shipping_city: order?.shipping_city,
                    shipping_country: order?.shipping_country,
                    shipping_state: order?.shipping_state,
                    shipping_zip_code: order?.shipping_zip_code,
                    pay_with_credit_card: values.pay_with_credit_card,
                    credit_card_token: creditCardToken,

                    po_files: values?.po_files?.map((f: any) => f.response.id),
                    notes: values.notes,
                    line_items: order_items.map(item => {
                        return {
                            id: item.id,
                            quantity: item.quantity,
                        };
                    }),
                };

                setLoading(true);
                setErrors(null);

                await SalesOrderStore.reOrder(id, payload)
                    .then(async () => {
                        if (values.pay_with_credit_card && order) {
                            const payload = {
                                amount: String(order.grand_total),
                                customer_email: UserStore?.user?.default_contact?.email ?? '',
                                representative_email: UserStore?.user?.default_contact?.account_owner?.email ?? '',
                                notes: values.notes,
                                quotes: SalesOrderStore.quotes,
                                ship_cost: '0',
                                tax_cost: '0',
                                zip_code: String(order.billing_zip_code ?? ''),
                                // files: values?.files ? values?.files?.fileList?.map(f => f.originFileObj) : [],
                            };

                            await SalesOrderStore.sendCreditCard(payload).catch(errorInfo => {
                                notification.error({
                                    duration: 10,
                                    message: 'Error',
                                    description: 'Credit Card Submission Error',
                                    placement: 'topRight',
                                });
                            });
                        }

                        history.push({
                            pathname: '/my-account/orders/reorder/success/',
                            state: {
                                status: 'success',
                                title: 'Thank you for submitting your Purchase Order!',
                                subTitle: 'Your Order will be available in your Orders tab soon',
                                // extra: [
                                //     <Link to="/my-account/quotes/new-quote"><Button key="new-quote">Submit a Quote</Button></Link>,
                                //     <Link to="/my-account/quotes"><Button key="review-quote">Review your Quotes</Button></Link>
                                // ]
                            },
                        });
                    })
                    .catch(error => {
                        setLoading(false);
                        setErrors(error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            })
            .catch(errorInfo => {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: 'Required fields are empty!',
                    placement: 'topRight',
                });
            });
    };

    const handleRemove = (id: number) => {
        const new_orders = order_items.filter(item => item.id !== id);

        const subtotalNumber = new_orders.reduce((sum, item) => {
            const price =
                typeof item.total_after_discount === 'number'
                    ? item.total_after_discount
                    : Number(item.total_after_discount) || 0;
            return sum + price;
        }, 0);

        const discountNumber = new_orders.reduce((sum, item) => {
            const price = typeof item.discount === 'number' ? item.discount : Number(item.discount) || 0;
            return sum + price;
        }, 0);

        const taxes = new_orders.reduce((sum, item) => {
            const price = typeof item.taxes === 'number' ? item.taxes : Number(item.taxes) || 0;
            return sum + price;
        }, 0);

        const discount: string = discountNumber.toFixed(2);
        const netSubtotal = Math.max(0, subtotalNumber - discountNumber);
        const subtotal: string = netSubtotal.toFixed(2);

        const grand_total = Number((Number(netSubtotal) + Number(taxes)).toFixed(2));

        if (order) {
            SalesOrderStore.setOrder({ ...order, subtotal, discount, taxes, grand_total });
        }

        SalesOrderStore.setOrderItems(new_orders);
    };

    return (
        <>
            <PageHeader>
                <div className="d-flex justify-start align-items-baseline">
                    <Button
                        type="link"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => {
                            history.goBack();
                        }}
                        className="text-primary m-0 p-0"
                    />
                    <Title level={1} className="mb-0">
                        Reordering - #<strong>{order?.name}</strong>
                    </Title>
                </div>
            </PageHeader>

            <div className="container">
                <Form
                    className={'sales-order-form'}
                    layout="vertical"
                    form={form}
                    scrollToFirstError={true}
                    onFinish={values => {
                        if (!values.pay_with_credit_card && !values.po_files) {
                            message.error('Please upload Purchase Order Files');
                        } else {
                            if (values.pay_with_credit_card) {
                                if (buttonReference.current) {
                                    buttonReference.current.click();
                                }
                            } else {
                                handleSubmit();
                            }
                        }
                    }}
                    onFinishFailed={errorInfo => {
                        if (buttonReference.current) {
                            buttonReference.current.click();
                        }

                        notification.error({
                            key: 'form-errors',
                            duration: 8,
                            message: 'Please input all required fields',
                            description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                            placement: 'top',
                            maxCount: 1,
                        });

                        // form.scrollToField('billing_address');
                    }}
                >
                    <Row gutter={[16, 8]}>
                        <Col md={12} span={24}>
                            <Card bordered={false}>
                                <div>
                                    <label>
                                        <span className="font-weight-bold" style={{ color: '#455560' }}>
                                            Billing Address
                                        </span>
                                    </label>
                                    <Skeleton
                                        active
                                        title={false}
                                        loading={order ? false : true}
                                        paragraph={{ rows: 3 }}
                                    >
                                        {order && <Addresses order={order} type={'Billing'} hideEditButton />}
                                    </Skeleton>
                                </div>
                                <div className="mt-5">
                                    <label>
                                        <span className="font-weight-bold" style={{ color: '#455560' }}>
                                            Shipping Address
                                        </span>
                                    </label>

                                    <Skeleton
                                        active
                                        title={false}
                                        loading={order ? false : true}
                                        paragraph={{ rows: 3 }}
                                    >
                                        {order && <Addresses order={order} type={'Shipping'} hideEditButton />}
                                    </Skeleton>
                                </div>

                                <div className={'flex flex-col w-full mt-5'}>
                                    <Form.Item
                                        dependencies={['pay_with_credit_card']} // if using dependencies
                                        noStyle
                                    >
                                        {({ getFieldValue }) => {
                                            // getFieldValue defined here
                                            return (
                                                <Form.Item
                                                    label="Purchase Order Files"
                                                    style={{
                                                        marginBottom: 1,
                                                    }}
                                                    name="po_files"
                                                    valuePropName="fileList"
                                                    rules={[
                                                        {
                                                            required: !getFieldValue('pay_with_credit_card'),
                                                            message: 'Please upload a purchase order file',
                                                        },
                                                    ]}
                                                >
                                                    <UploadFile multiple />
                                                </Form.Item>
                                            );
                                        }}
                                    </Form.Item>
                                </div>
                            </Card>
                        </Col>
                        <Col md={12} span={24}>
                            <Card>
                                <div className="review-items">
                                    <div className="font-weight-bold font-size-lg">Order Review</div>
                                    <Spin spinning={SalesOrderStore.loading}>
                                        <Row className={'row-order'}>
                                            {order_items.map(item => {
                                                return (
                                                    <React.Fragment key={item.id}>
                                                        <div
                                                            key={item.id}
                                                            className="d-flex flex-column justify-end w-100"
                                                        >
                                                            <div className="d-flex flex-column">
                                                                <div
                                                                    className="d-flex flex-row  align-items-center w-100"
                                                                    style={{ minHeight: '50px' }}
                                                                >
                                                                    {item.quote_has_product && (
                                                                        <div className="d-none d-sm-block mr-4">
                                                                            <PartPreview
                                                                                thumbnail={
                                                                                    item.quote_has_product.thumbnail
                                                                                }
                                                                                has_file_3d={
                                                                                    item.quote_has_product.has_file_3d
                                                                                }
                                                                                width="75px"
                                                                            />
                                                                        </div>
                                                                    )}

                                                                    <div className="font-size-base font-weight-bold mb-1">
                                                                        {item.display_name}
                                                                    </div>

                                                                    <div className="d-flex align-items-start justify-content-end ml-auto">
                                                                        <Price
                                                                            value={item.total_after_discount}
                                                                            style={{
                                                                                fontSize: 16,
                                                                                fontWeight: 400,
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="d-flex flex-row items-end justify-content-between">
                                                                <div style={{ width: '220px' }}>
                                                                    {!item.quote_has_product ? (
                                                                        <>
                                                                            <div className="font-thin">
                                                                                {/*{item.product_description}*/}
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <div className="d-flex flex-column">
                                                                                <div className="d-flex flex-row mr-2">
                                                                                    <div className="font-weight-bold">
                                                                                        Material:
                                                                                    </div>
                                                                                    <div className="font-thin ml-1">
                                                                                        {
                                                                                            item.quote_has_product
                                                                                                ?.material_related?.name
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                                <div className="d-flex flex-row mr-2">
                                                                                    <div className="font-weight-bold">
                                                                                        Finish:
                                                                                    </div>
                                                                                    <div className="font-thin ml-1">
                                                                                        {
                                                                                            item?.quote_has_product.finish_related?.name
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                                <div className="d-flex flex-row mr-2">
                                                                                    <div className="font-weight-bold">
                                                                                        Coating:
                                                                                    </div>
                                                                                    <div className="font-thin ml-1">
                                                                                        {
                                                                                            item.quote_has_product
                                                                                            ?.coating_related?.name
                                                                                        }
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    )}

                                                                    <div className="actions">
                                                                        <Button
                                                                            type="link"
                                                                            className="p-0 m-0"
                                                                            onClick={() => {
                                                                                handleRemove(item.id);
                                                                            }}
                                                                        >
                                                                            Remove
                                                                        </Button>
                                                                    </div>
                                                                </div>

                                                                <div className="d-flex flex-column-reverse justify-content-end flex-lg-row">
                                                                    <div className="d-flex flex-column align-items-start justify-content-start">
                                                                        <span className="font-weight-bold mr-2 mb-1">
                                                                            Quantity:
                                                                        </span>

                                                                        <Form.Item
                                                                            name={['order_items', item.id, 'quantity']}
                                                                            label="Quantity"
                                                                            className="d-flex flex-row-row-reverse"
                                                                            rules={[
                                                                                {
                                                                                    required: true,
                                                                                    message: 'Quantity is required!',
                                                                                },
                                                                            ]}
                                                                            initialValue={item.quantity}
                                                                            noStyle
                                                                        >
                                                                            <div className={'text-center w-full'}>
                                                                                {item.quantity}
                                                                            </div>
                                                                        </Form.Item>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center text-gray-500">
                                                            {item.quote_has_product?.is_fastlane_safe &&
                                                                item.quote_has_product?.material_disclaimer_message}
                                                        </div>

                                                        <Divider className="mt-1 mb-1" />
                                                    </React.Fragment>
                                                );
                                            })}
                                        </Row>
                                    </Spin>
                                </div>
                                <div className="billing-summary">
                                    <div
                                        className="font-weight-bold my-4"
                                        style={{
                                            fontSize: 18,
                                        }}
                                    >
                                        Order Summary
                                    </div>
                                    <div className={'d-flex flex-col justify-between'}>
                                        <div className="d-flex flex-row justify-between  mb-1">
                                            <div className="summary-label">Subtotal</div>
                                            <div className="summary-value">
                                                <Price value={order?.subtotal ?? 0} />
                                            </div>
                                        </div>

                                        <div className="d-flex flex-row justify-between mt-1">
                                            <div className="summary-label">Discount</div>
                                            <div className="summary-value">
                                                <Price value={order?.discount ?? 0} />
                                            </div>
                                        </div>

                                        <div className="d-flex flex-row justify-between  mt-2">
                                            <div className="summary-label">Tax</div>
                                            <div className="summary-value">
                                                <Price value={order?.taxes ?? 0} />
                                            </div>
                                        </div>

                                        <div
                                            className="d-flex flex-row justify-between  mt-2 pt-4"
                                            style={{
                                                borderTop: 'solid 1px #d9d9d9',
                                            }}
                                        >
                                            <div className="font-weight-bold text-xl">Grand Total</div>
                                            <div className="font-weight-bold text-xl">
                                                <Price value={order?.grand_total ?? 0} />
                                            </div>
                                        </div>
                                    </div>

                                    <p
                                        className="pt-4 font-bold"
                                        style={{
                                            textAlign: 'left', // optional for text alignment
                                        }}
                                    >
                                        *Your order will include a Material Certificate and Certificate of Conformance.
                                    </p>

                                    {!UserStore?.user?.default_contact?.is_hobbyist && (
                                        <div className="order-notes mt-4">
                                            <Form.Item label="Order Comments" name="notes">
                                                <Input.TextArea placeholder={'Type here...'} />
                                            </Form.Item>
                                        </div>
                                    )}

                                    <div className="additional-information">
                                        <div
                                            className="font-weight-bold my-4"
                                            style={{
                                                fontSize: 18,
                                            }}
                                        >
                                            Payment Method
                                        </div>
                                        <div className="square-payment">
                                            <div className={'payment d-flex flex-row items-center'}>
                                                <div className={'w-full'}>
                                                    <Form.Item
                                                        label=""
                                                        className="mx-0 md:mx-2"
                                                        name="pay_with_credit_card"
                                                        valuePropName="checked"
                                                        rules={[
                                                            {
                                                                required: false,
                                                                message: 'Field is required',
                                                            },
                                                        ]}
                                                        initialValue={true}
                                                    >
                                                        <Checkbox
                                                            disabled={UserStore?.user?.default_contact?.is_hobbyist}
                                                        >
                                                            Pay With Credit Card
                                                        </Checkbox>
                                                    </Form.Item>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <Form.Item
                                        dependencies={['pay_with_credit_card']} // if using dependencies
                                        noStyle
                                    >
                                        {({ getFieldValue }) => {
                                            // getFieldValue defined here
                                            return getFieldValue('pay_with_credit_card') ? ( // reference getFieldValue here
                                                <div className="text-left mt-2">
                                                    <div>
                                                        <SquarePaymentForm
                                                            buttonRef={buttonReference}
                                                            app_id={SQUARE_APP_ID}
                                                            location_id={SQUARE_LOCATION_ID}
                                                            script_url={SQUARE_ENVIRONMENT_URL}
                                                            onPayment={sourceId => {
                                                                if (sourceId) {
                                                                    handleSubmit(sourceId);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            ) : null;
                                        }}
                                    </Form.Item>
                                    <div className="text-center mt-2">
                                        <Button
                                            id="place-order-btn"
                                            loading={loading}
                                            size="large"
                                            type="primary"
                                            htmlType="submit"
                                            block={true}
                                        >
                                            <span className="mr-1">Place Order</span>
                                            <Price value={order?.grand_total ?? 0} />
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            </div>
        </>
    );
};

export default observer(Reorder);
